/**
 * practice — client feature module (spec §4.1, brief §practice).
 * Everything this feature contributes to the app travels through this file:
 * routes, top-nav entries and its slice of the dictionaries.
 *
 * Routes use react-router's `Component` field so this file stays plain `.ts`.
 */
import { Target } from "lucide-react";
import type { FeatureModule } from "@client/app/types";
import { PracticeExamGuard } from "./ExamGuard";
import { PracticePage } from "./Page";
import { PracticeSessionPage } from "./SessionPage";
import { contrastDict } from "./contrastDict";
import { setsDict } from "./sets/dict";
import { WhichPrinciplePage } from "./sets/WhichPrinciplePage";
import { todayDict } from "./today/dict";
import { TodayPage } from "./today/TodayPage";

const practiceFeature: FeatureModule = {
  id: "practice",
  // A single top-level path ("/practice") with nested children — not two
  // separate top-level routes — so this feature still contributes exactly
  // one entry to `client/src/app/router.test.tsx`'s
  // "matches every route every feature registers" check (frozen: one path
  // per feature). The parent has no `element`, so react-router defaults it
  // to `<Outlet />` and the index/`session`/`today`/`principles` children render normally at
  // `/practice`, `/practice/session`, `/practice/today` (Today's runner) and
  // `/practice/principles` (the "Vilken princip?" set of the exam wrapper).
  routes: [
    {
      path: "/practice",
      // P2-12: during an övningstenta the whole tree is replaced by the guard.
      Component: PracticeExamGuard,
      children: [
        { index: true, Component: PracticePage },
        { path: "session", Component: PracticeSessionPage },
        { path: "today", Component: TodayPage },
        { path: "principles", Component: WhichPrinciplePage },
      ],
    },
  ],
  nav: [
    { to: "/practice", labelKey: "practice.nav", icon: Target, order: 10, hideDuringExam: true },
  ],
  dict: {
    sv: {
      ...todayDict.sv,
      ...contrastDict.sv,
      ...setsDict.sv,
      "practice.link.ignored":
        "Inaktuella eller oanvända filter har utelämnats: {filters}. Kontrollera urvalet innan du startar.",
      "practice.nav": "Öva",
      "practice.examLocked.title": "Inte tillgängligt under övningstentan",
      "practice.examLocked.body":
        "Medan du skriver övningstentan är övningsfrågorna stängda, precis som på riktig tenta. De öppnas igen när du har lämnat in.",
      "practice.examLocked.back": "Tillbaka till övningstentan",
      "practice.session.questionGone": "Frågan finns inte längre, vi hoppar över den.",
      "practice.resume.title": "Du har en påbörjad omgång",
      "practice.resume.body": "Du är på fråga {position} av {total} ({answered} klara).",
      "practice.resume.continue": "Fortsätt din omgång (fråga {position} av {total})",
      "practice.resume.discard": "Släng omgången",
      "practice.link.invalid":
        "Urvalet i länken är ogiltigt. Kontrollera kurs, föreläsning och filter.",
      "practice.link.reset": "Till övningssidan",
      "practice.lecture.notFound": "Kursen eller föreläsningen hittades inte.",
      "practice.lecture.error":
        "Det gick inte att hämta övningarna. Försök igen eller öppna länken på nytt.",
      "practice.lecture.pool": "{count} tillgängliga frågor",
      "practice.lecture.opens": "Öppnas {date}. Frågorna går att öva från den dagen.",
      "practice.lecture.closed": "Stängd tills vidare. Läraren har inte öppnat frågorna än.",
      "practice.topic.opens":
        "Ämnet öppnas {date}, när föreläsningens frågor öppnas. Tills dess går det inte att öva på det.",
      "practice.topic.closed":
        "Stängd tills vidare. Läraren har inte öppnat frågorna till ämnet än.",
      "practice.lecture.unpublished": "Övningarna har ännu inte publicerats.",
      "practice.lecture.empty":
        "Inga frågor är tillgängliga just nu. Om du har ett pågående prov behöver du avsluta det och öppna länken igen.",
      "practice.setup.description": "Öva fritt eller välj moment och ämnen själv.",
      "practice.setup.mode": "Övningsläge",
      "practice.setup.chapters": "Moment",
      "practice.setup.topics": "Ämnen",
      "practice.setup.types": "Typ av uppgift",
      "practice.setup.difficulty": "Svårighetsgrad",
      "practice.setup.count": "Antal frågor",
      "practice.setup.start": "Starta",
      "practice.setup.noQuestions":
        "Inga frågor matchade urvalet. Prova att välja fler moment eller ämnen.",
      "practice.taught.label": "Bara det vi gått igenom (t.o.m. {id})",
      "practice.taught.hint":
        "Bara ämnen som föreläsningarna har tagit upp hittills. Avmarkera för att öva på allt som är öppet.",
      "practice.taught.examMode": "Av i tentaläget: du övar på allt som är öppet.",
      "practice.taught.fallback":
        "Det vi gått igenom t.o.m. {id} räcker inte till hela omgången än, så du får frågor från alla öppna ämnen.",
      "practice.mode.mixed": "Blandat",
      "practice.mode.topic": "Välj ämne",
      "practice.type.mc": "Snabbkoll (flerval)",
      "practice.type.numeric": "Snabbkoll (räkna)",
      "practice.type.open": "Uppgift (som på tentan)",
      "practice.difficulty.1": "Lätt",
      "practice.difficulty.2": "Medel",
      "practice.difficulty.3": "Svår",
      "practice.hint.show": "Visa ledtråd",
      "practice.hint.title": "Kört fast?",
      "practice.hint.note": "Använd gärna ledtråd om du fastnar.",
      "practice.hint.level.principle": "Princip",
      "practice.hint.level.setup": "Uppställning",
      "practice.hint.level.first-line": "Första raden",
      "practice.hint.showLevel": "Visa ledtråd: {level}",
      "practice.hint.afterPlan": "Ledtrådarna öppnas när du har valt princip.",
      "practice.plan.legend": "Vilken princip ska du använda?",
      "practice.plan.note":
        "Att först sätta namn på principen är en del av övningen. Valet påverkar inte poängen. Väljer du fel visar vi rätt princip, och då räknas svaret som övning med hjälp.",
      "practice.plan.right": "Ja, uppgiften handlar om {principle}.",
      "practice.plan.wrong": "Inte riktigt. Uppgiften handlar främst om {principle}.",
      "practice.plan.yourPick": "Du valde {principle}.",
      "practice.plan.chooseFirst": "Välj en princip innan du börjar räkna.",
      "practice.session.heading": "Övning · fråga {current} av {total}",
      "practice.session.progress": "Framsteg i passet",
      "practice.session.problemRegion": "Uppgift",
      "practice.session.workRegion": "Ditt arbete",
      "practice.formulas.toggle": "Formler för uppgiften",
      "practice.formulas.afterPlan": "Formlerna visas när du har valt princip.",
      "practice.formulas.empty": "Det finns inga formler för det här ämnet än.",
      "practice.formulas.all": "Alla formler",
      "practice.formulas.newTab": "(öppnas i ny flik)",
      "practice.formulas.examNote":
        "På tentan har du Physics Handbook, tabeller, miniräknare och ett eget handskrivet A4-blad.",
      "practice.submit": "Svara",
      "practice.confidence.required": "Välj hur säker du är innan du svarar.",
      "practice.confidence.feedback.wrongSure":
        "Du var säker, men svaret blev fel. Läs lösningen extra noga — just de här misstagen är lättast att rätta till.",
      "practice.confidence.feedback.rightUnsure":
        "Rätt, trots att du var osäker. Vad gjorde att du tvekade?",
      "practice.numeric.answer": "Svar",
      "practice.mc.justification": "Motivering (valfritt): varför är ditt val rätt?",
      "practice.mc.justificationRequired": "Motivering: varför är ditt val rätt?",
      "practice.mc.justificationMissing": "Den här frågan kräver en motivering innan du svarar.",
      "practice.open.yourWorking": "Din lösning",
      "practice.open.yourWorkingHint": "Skriv (eller räkna på papper) innan du visar lösningen.",
      "practice.open.klar": "Klar — visa lösning",
      "practice.open.giveUp": "Ge upp",
      "practice.open.save": "Spara min poäng",
      "practice.open.rubric": "Bedömningspunkter",
      "practice.open.preRevealLabel":
        "Ditt svar innan du visade lösningen (kan inte längre ändras)",
      "practice.open.preRevealEmpty":
        "Du skrev inget här innan du visade lösningen. Bedöm det du räknade på papper.",
      "practice.open.correction": "Rättelse efter att du har sett lösningen (valfritt)",
      "practice.open.missed": "Vilken princip eller vilket villkor missade du?",
      "practice.open.missedOptional": "Vilken princip eller vilket villkor missade du? (valfritt)",
      "practice.open.missedRequired":
        "Skriv vad du missade — du har inte bockat av alla bedömningspunkter.",
      "practice.open.missedChips": "Snabbsvar: vad gick fel?",
      "practice.open.missedChip.wrongPrinciple": "Fel princip",
      "practice.open.missedChip.wrongSetup": "Rätt princip, fel uppställning",
      "practice.open.missedChip.calcError": "Räknefel",
      "practice.open.missedChip.dontKnow": "Vet inte än",
      "practice.confidence.keysHint": "Välj säkerhet (1–4) och tryck Enter",
      "practice.open.rubricInstruction":
        "Bocka bara av det som fanns med i ditt svar innan du visade lösningen.",
      "practice.feedback.correct": "Rätt!",
      "practice.feedback.partial": "Delvis rätt",
      "practice.feedback.incorrect": "Fel",
      "practice.feedback.gaveUp": "Löst utan poäng",
      "practice.feedback.notYet": "Inte rätt än",
      "practice.feedback.correctOnRetry": "Rätt i andra försöket",
      "practice.feedback.incorrectAfterRetry": "Inte rätt i andra försöket heller",
      "practice.feedback.solutionLocked": "Lösningen öppnas efter ditt andra försök.",
      "practice.feedback.yourAnswerConfidence": "Ditt svar {value} · du var {level}",
      "practice.feedback.yourAnswer": "Ditt svar:",
      "practice.feedback.expected": "Rätt svar:",
      "practice.feedback.partCorrect": "Rätt delsvar",
      "practice.feedback.partIncorrect": "Fel delsvar",
      "practice.feedback.partCorrectSingle": "Rätt",
      "practice.feedback.partIncorrectSingle": "Fel",
      "practice.feedback.optionCorrect": "Rätt svar",
      "practice.feedback.optionYourChoice": "Ditt val",
      "practice.feedback.solution": "Lösning",
      "practice.topicChip.readMore": "Läs mer:",
      "practice.topicChip.newTab": "(öppnas i ny flik)",
      "practice.feedback.yourJustification": "Din motivering",
      "practice.feedback.reflectPrompt":
        "Innan du läser lösningen: vilken princip eller vilket samband borde du ha använt, och var gick det fel?",
      "practice.feedback.showSolution": "Visa lösningen",
      "practice.feedback.tryRelated": "Öva på en liknande uppgift",
      "practice.feedback.noRelated": "Det finns ingen ny uppgift i det här ämnet just nu.",
      "practice.feedback.relatedAdded": "En liknande uppgift kommer härnäst.",
      "practice.nearMiss.powerOfTen": "Rätt siffror, fel tiopotens.",
      "practice.nearMiss.unit": "Rätt värde, men i {from}; rutan vill ha {to}.",
      "practice.nearMiss.unitMismatch": "{typed} är inte enheten rutan vill ha ({expected}).",
      "practice.nearMiss.sign": "Rätt storlek, fel tecken.",
      "practice.nearMiss.factorTwo":
        "Ditt svar är dubbelt så stort som det borde vara. Leta efter en faktor 2 eller ½ som fattas eller är för mycket.",
      "practice.nearMiss.factorHalf":
        "Ditt svar är hälften så stort som det borde vara. Leta efter en faktor 2 eller ½ som fattas eller är för mycket.",
      "practice.nearMiss.factorTwoPi":
        "Ditt svar skiljer sig med en faktor 2π. Har du blandat ihop h och ħ, eller f och ω?",
      // A pending retry names the process, never the direction or the unit names (spec §5.3.4).
      "practice.nearMiss.pending.sign": "Kontrollera tecknet: åt vilket håll ska storheten ändras?",
      "practice.nearMiss.pending.factor":
        "Kontrollera om en faktor 2, ½ eller 2π saknas eller är för mycket, till exempel i ½mv² eller mellan h och ħ.",
      "practice.nearMiss.pending.unit":
        "Rätt värde, men i en annan enhet än rutans ({unit}). Räkna om det.",
      "practice.nearMiss.pending.unitMismatch":
        "Enheten du skrev går inte att räkna om till rutans ({unit}).",
      "practice.diagnosis.genericNumeric":
        "Fundera på vilken princip som gäller och hur du ställde upp sambandet – en ledtråd kan hjälpa.",
      "practice.diagnosis.genericMc":
        "Det alternativet stämmer inte. Läs frågan igen: vilken princip avgör svaret?",
      "practice.retry.title": "Försök igen",
      "practice.retry.caption":
        "Ett försök till. Blir det rätt nu räknas uppgiften som löst med hjälp.",
      "practice.retry.submit": "Kontrollera igen",
      "practice.retry.stuck": "Jag kör fast – visa lösningen",
      "practice.retry.partKept": "Rätt – behålls",
      "practice.retry.firstChoice": "Ditt första val",
      "practice.retry.failed":
        "Det gick inte att spara ett nytt försök. Lösningen visas i stället.",
      "practice.retry.autoHint": "Här är en ledtråd till nästa försök.",
      "practice.retry.unreadable":
        "Ett av svaren går inte att tolka som ett tal. Rätta det och kontrollera igen.",
      "practice.selfExplain.label": "Förklara för dig själv (valfritt)",
      "practice.selfExplain.hint": "En mening räcker. Bara du ser den, i din egen historik.",
      "practice.selfExplain.promptWrong": "Vad i uppställningen ledde till ditt första svar?",
      "practice.selfExplain.promptAfter": "Förklara med en mening varför svaret blir som det blir.",
      "practice.selfExplain.save": "Spara",
      "practice.selfExplain.saved": "Sparad",
      "practice.selfExplain.failed":
        "Det gick inte att spara din förklaring. Du kan gå vidare ändå.",
      "practice.part.heading": "Deluppgift ({part})",
      "practice.part.check": "Rätta deluppgift ({part})",
      "practice.part.locked": "Låses upp när du har svarat på deluppgift ({previous})",
      "practice.part.right": "Deluppgift ({part}): rätt.",
      "practice.part.wrong":
        "Deluppgift ({part}): fel. Du får försöka igen på den när du har svarat på alla deluppgifter.",
      "practice.part.checkRest":
        "Rätta resten nu (deluppgifter du inte kontrollerat räknas som tomma)",
      "practice.part.dependsOnWrong":
        "Deluppgift ({part}) kan bygga på ({previous}). Du kan ändra båda i nästa försök.",
      "practice.part.showAll": "Visa alla deluppgifter på en gång",
      "practice.part.showAllHint":
        "För dig som redan kan området: svara på alla deluppgifter och rätta dem tillsammans.",
      "practice.part.confidence": "Hur säker är du på deluppgift ({part})?",
      "practice.part.valueRequired": "Skriv ett tal innan du rättar deluppgiften.",
      "practice.summary.title": "Sammanfattning",
      "practice.summary.score": "{score} av {maxScore} poäng",
      "practice.summary.gaveUp": "Gav upp",
      "practice.summary.withHelp": "Rätt med hjälp",
      "practice.summary.firstTry":
        "Poängen bygger på ditt första försök. Det du fick rätt i andra försöket markeras ”Rätt med hjälp”.",
      "practice.summary.tryRelated": "Öva på liknande uppgifter ({count})",
      "practice.summary.noRelated": "Det finns inga nya liknande uppgifter just nu.",
      "practice.summary.newSession": "Ny övning",
      "practice.recent.title": "Senaste övningar",
      "practice.recent.empty": "Du har inte övat ännu — starta din första omgång ovan.",
      "practice.recent.retry": "Öva igen",
      "practice.recent.retryAria": "Öva uppgiften i {topic} igen",
      "practice.recent.notes": "Dina anteckningar",
      "practice.recent.notesAria": "Dina anteckningar till uppgiften i {topic}",
      "practice.recent.reflection.justification": "Motivering",
      "practice.recent.reflection.text": "Ditt svar innan lösningen",
      "practice.recent.reflection.correction": "Rättelse",
      "practice.recent.reflection.missed": "Det du missade",
      "practice.recent.reflection.selfExplanation": "Din förklaring",
      "practice.recent.reflection.retrySelfExplanation": "Din förklaring efter andra försöket",
    },
    en: {
      ...todayDict.en,
      ...contrastDict.en,
      ...setsDict.en,
      "practice.link.ignored":
        "Outdated or unused filters were ignored: {filters}. Check the selection before starting.",
      "practice.nav": "Practice",
      "practice.examLocked.title": "Not available during the practice exam",
      "practice.examLocked.body":
        "While you write the practice exam the practice questions are closed, as in the real exam. They open again once you hand in.",
      "practice.examLocked.back": "Back to the exam",
      "practice.link.invalid":
        "Invalid practice selection. Check the course, lecture and filters in the link.",
      "practice.link.reset": "Go to practice",
      "practice.lecture.notFound": "Course or lecture not found.",
      "practice.lecture.error": "The selection could not be loaded. Try again or reopen the link.",
      "practice.lecture.pool": "{count} available questions",
      "practice.lecture.opens": "Opens {date}. The questions can be practised from that day.",
      "practice.lecture.closed":
        "Closed until further notice. The teacher has not opened the questions yet.",
      "practice.topic.opens":
        "This topic opens {date}, when the lecture's questions open. Until then it cannot be practised.",
      "practice.topic.closed":
        "Closed until further notice. The teacher has not opened this topic's questions yet.",
      "practice.lecture.unpublished": "The practice selection has not been published yet",
      "practice.lecture.empty":
        "No questions are available right now. Finish any active exam and reopen the link.",
      "practice.session.questionGone": "That question no longer exists, so we skip it.",
      "practice.resume.title": "You have a set in progress",
      "practice.resume.body": "You are on question {position} of {total} ({answered} done).",
      "practice.resume.continue": "Continue your set (question {position} of {total})",
      "practice.resume.discard": "Discard the set",
      "practice.setup.description": "Practice freely, or pick modules and topics yourself.",
      "practice.setup.mode": "Practice mode",
      "practice.setup.chapters": "Modules",
      "practice.setup.topics": "Topics",
      "practice.setup.types": "Question type",
      "practice.setup.difficulty": "Difficulty",
      "practice.setup.count": "Number of questions",
      "practice.setup.start": "Start",
      "practice.setup.noQuestions":
        "No questions matched that selection. Try more modules or topics.",
      "practice.taught.label": "Only what we have covered (up to {id})",
      "practice.taught.hint":
        "Only topics the lectures have covered so far. Untick to practise everything that is open.",
      "practice.taught.examMode": "Off in exam mode: you practise everything that is open.",
      "practice.taught.fallback":
        "What we have covered up to {id} is not enough for a full set yet, so you get questions from every open topic.",
      "practice.mode.mixed": "Mixed",
      "practice.mode.topic": "Choose topics",
      "practice.type.mc": "Quick check (multiple choice)",
      "practice.type.numeric": "Quick check (numeric)",
      "practice.type.open": "Problem (exam-style)",
      "practice.difficulty.1": "Easy",
      "practice.difficulty.2": "Medium",
      "practice.difficulty.3": "Hard",
      "practice.hint.show": "Show hint",
      "practice.hint.title": "Stuck?",
      "practice.hint.note": "Feel free to use a hint if you get stuck.",
      "practice.hint.level.principle": "Principle",
      "practice.hint.level.setup": "Set-up",
      "practice.hint.level.first-line": "First line",
      "practice.hint.showLevel": "Show hint: {level}",
      "practice.hint.afterPlan": "Hints open once you have chosen a principle.",
      "practice.plan.legend": "Which principle will you use?",
      "practice.plan.note":
        "Naming the principle first is part of the exercise. Your choice does not affect the score. If you pick the wrong one, we show the right principle, and the answer then counts as practice with help.",
      "practice.plan.right": "Yes, this problem is about {principle}.",
      "practice.plan.wrong": "Not quite. This problem is mainly about {principle}.",
      "practice.plan.yourPick": "You picked {principle}.",
      "practice.plan.chooseFirst": "Choose a principle before you start calculating.",
      "practice.session.heading": "Practice · question {current} of {total}",
      "practice.session.progress": "Session progress",
      "practice.session.problemRegion": "Problem",
      "practice.session.workRegion": "Your work",
      "practice.formulas.toggle": "Formulas for this problem",
      "practice.formulas.afterPlan": "The formulas appear once you have chosen a principle.",
      "practice.formulas.empty": "There are no formulas for this topic yet.",
      "practice.formulas.all": "All formulas",
      "practice.formulas.newTab": "(opens in a new tab)",
      "practice.formulas.examNote":
        "In the exam you have Physics Handbook, tables, a calculator and one hand-written A4 sheet of your own.",
      "practice.submit": "Submit",
      "practice.confidence.required": "Choose how sure you are before you submit.",
      "practice.confidence.feedback.wrongSure":
        "You were sure, but the answer was wrong. Read the solution extra carefully — these mistakes are the easiest to fix.",
      "practice.confidence.feedback.rightUnsure":
        "Correct, even though you were unsure. What made you hesitate?",
      "practice.numeric.answer": "Answer",
      "practice.mc.justification": "Justification (optional): why is your choice correct?",
      "practice.mc.justificationRequired": "Justification: why is your choice correct?",
      "practice.mc.justificationMissing": "This question needs a justification before you submit.",
      "practice.open.yourWorking": "Your working",
      "practice.open.yourWorkingHint":
        "Write it out (or work on paper) before you show the solution.",
      "practice.open.klar": "Done — show solution",
      "practice.open.giveUp": "Give up",
      "practice.open.save": "Save my score",
      "practice.open.rubric": "Rubric",
      "practice.open.preRevealLabel":
        "Your answer before you showed the solution (can no longer be changed)",
      "practice.open.preRevealEmpty":
        "You wrote nothing here before showing the solution. Assess the work you did on paper.",
      "practice.open.correction": "Correction after seeing the solution (optional)",
      "practice.open.missed": "Which principle or condition did you miss?",
      "practice.open.missedOptional": "Which principle or condition did you miss? (optional)",
      "practice.open.missedRequired":
        "Write what you missed — you have not ticked every rubric point.",
      "practice.open.missedChips": "Quick answer: what went wrong?",
      "practice.open.missedChip.wrongPrinciple": "Wrong principle",
      "practice.open.missedChip.wrongSetup": "Right principle, wrong set-up",
      "practice.open.missedChip.calcError": "Calculation error",
      "practice.open.missedChip.dontKnow": "Don't know yet",
      "practice.confidence.keysHint": "Choose confidence (1–4) and press Enter",
      "practice.open.rubricInstruction":
        "Tick only what your answer contained before you showed the solution.",
      "practice.feedback.correct": "Correct!",
      "practice.feedback.partial": "Partially correct",
      "practice.feedback.incorrect": "Incorrect",
      "practice.feedback.gaveUp": "Revealed, ungraded",
      "practice.feedback.notYet": "Not yet",
      "practice.feedback.correctOnRetry": "Right on the second try",
      "practice.feedback.incorrectAfterRetry": "Not right on the second try either",
      "practice.feedback.solutionLocked": "The solution opens after your second try.",
      "practice.feedback.yourAnswerConfidence": "Your answer {value} · you were {level}",
      "practice.feedback.yourAnswer": "Your answer:",
      "practice.feedback.expected": "Expected:",
      "practice.feedback.partCorrect": "Part correct",
      "practice.feedback.partIncorrect": "Part incorrect",
      "practice.feedback.partCorrectSingle": "Correct",
      "practice.feedback.partIncorrectSingle": "Incorrect",
      "practice.feedback.optionCorrect": "Correct answer",
      "practice.feedback.optionYourChoice": "Your choice",
      "practice.feedback.solution": "Solution",
      "practice.topicChip.readMore": "Read more:",
      "practice.topicChip.newTab": "(opens in a new tab)",
      "practice.feedback.yourJustification": "Your justification",
      "practice.feedback.reflectPrompt":
        "Before you read the solution: which principle or relation should you have used, and where did it go wrong?",
      "practice.feedback.showSolution": "Show the solution",
      "practice.feedback.tryRelated": "Practise a similar problem",
      "practice.feedback.noRelated": "There is no new problem in this topic right now.",
      "practice.feedback.relatedAdded": "A similar problem comes next.",
      "practice.nearMiss.powerOfTen": "Right digits, wrong power of ten.",
      "practice.nearMiss.unit": "Right value, but in {from}; the box wants {to}.",
      "practice.nearMiss.unitMismatch": "{typed} is not the unit the box wants ({expected}).",
      "practice.nearMiss.sign": "Right size, wrong sign.",
      "practice.nearMiss.factorTwo":
        "Your answer is twice what it should be. Look for a factor of 2 or ½ that is missing or extra.",
      "practice.nearMiss.factorHalf":
        "Your answer is half what it should be. Look for a factor of 2 or ½ that is missing or extra.",
      "practice.nearMiss.factorTwoPi":
        "Your answer is off by a factor of 2π. Did you mix up h and ħ, or f and ω?",
      "practice.nearMiss.pending.sign": "Check the sign: which way should the quantity change?",
      "practice.nearMiss.pending.factor":
        "Check whether a factor of 2, ½ or 2π is missing or extra, for example in ½mv² or between h and ħ.",
      "practice.nearMiss.pending.unit":
        "Right value, but in a different unit than the box's ({unit}). Convert it.",
      "practice.nearMiss.pending.unitMismatch":
        "The unit you typed cannot be converted into the box's ({unit}).",
      "practice.diagnosis.genericNumeric":
        "Think about which principle applies and how you set up the relation – a hint may help.",
      "practice.diagnosis.genericMc":
        "That option is not right. Read the question again: which principle decides it?",
      "practice.retry.title": "Try again",
      "practice.retry.caption":
        "One more try. If you get it right now, it counts as solved with help.",
      "practice.retry.submit": "Check again",
      "practice.retry.stuck": "I'm stuck – show the solution",
      "practice.retry.partKept": "Correct – kept",
      "practice.retry.firstChoice": "Your first choice",
      "practice.retry.failed": "The second try could not be saved. The solution is shown instead.",
      "practice.retry.autoHint": "Here is a hint for your next try.",
      "practice.retry.unreadable":
        "One of the answers cannot be read as a number. Fix it and check again.",
      "practice.selfExplain.label": "Explain it to yourself (optional)",
      "practice.selfExplain.hint": "One sentence is enough. Only you see it, in your own history.",
      "practice.selfExplain.promptWrong": "What in your set-up led to your first answer?",
      "practice.selfExplain.promptAfter":
        "In one sentence, explain why the answer comes out as it does.",
      "practice.selfExplain.save": "Save",
      "practice.selfExplain.saved": "Saved",
      "practice.selfExplain.failed":
        "Your explanation could not be saved. You can carry on anyway.",
      "practice.part.heading": "Part ({part})",
      "practice.part.check": "Check part ({part})",
      "practice.part.locked": "Unlocks after you answer part ({previous})",
      "practice.part.right": "Part ({part}): correct.",
      "practice.part.wrong":
        "Part ({part}): wrong. You can try it again once you have answered every part.",
      "practice.part.checkRest": "Grade the rest now (unchecked parts count as blank)",
      "practice.part.dependsOnWrong":
        "Part ({part}) may build on part ({previous}). You can change both on the next try.",
      "practice.part.showAll": "Show all parts at once",
      "practice.part.showAllHint":
        "For when you know the topic: answer every part and check them together.",
      "practice.part.confidence": "How sure are you about part ({part})?",
      "practice.part.valueRequired": "Enter a number before you check the part.",
      "practice.summary.title": "Summary",
      "practice.summary.score": "{score} of {maxScore} points",
      "practice.summary.gaveUp": "Gave up",
      "practice.summary.withHelp": "Right with help",
      "practice.summary.firstTry":
        "The score is based on your first try. What you got right on the second try is marked “Right with help”.",
      "practice.summary.tryRelated": "Practise similar problems ({count})",
      "practice.summary.noRelated": "There are no new similar problems right now.",
      "practice.summary.newSession": "New session",
      "practice.recent.title": "Recent practice",
      "practice.recent.empty": "You have not practised yet — start your first round above.",
      "practice.recent.retry": "Practice again",
      "practice.recent.retryAria": "Practice the {topic} problem again",
      "practice.recent.notes": "Your notes",
      "practice.recent.notesAria": "Your notes on the {topic} problem",
      "practice.recent.reflection.justification": "Justification",
      "practice.recent.reflection.text": "Your answer before the solution",
      "practice.recent.reflection.correction": "Correction",
      "practice.recent.reflection.missed": "What you missed",
      "practice.recent.reflection.selfExplanation": "Your explanation",
      "practice.recent.reflection.retrySelfExplanation": "Your explanation after the second try",
    },
  },
};

export default practiceFeature;
